import { useState, useEffect, useRef } from 'react'
import { useUserStore } from '../../stores/userStore.js'

const API = import.meta.env.VITE_API_BASE ?? ''

// ── Duration picker options ──
const TIMEOUT_DURATIONS = [
  { label: '5 min',  value: '5m'  },
  { label: '10 min', value: '10m' },
  { label: '30 min', value: '30m' },
  { label: '1 hour', value: '1h'  },
  { label: '1 day',  value: '1d'  },
  { label: '7 days', value: '7d'  },
]

const REPORT_CATEGORIES = [
  { label: 'Spam',            value: 'spam'        },
  { label: 'Harassment',      value: 'harassment'   },
  { label: 'Misinformation',  value: 'misinfo'      },
  { label: 'Inappropriate',   value: 'inappropriate'},
  { label: 'Off-topic',       value: 'offtopic'     },
  { label: 'Other',           value: 'other'        },
]

// ── Main Context Menu ──
export function ContextMenu({ x, y, onClose, children }) {
  const ref = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    function handleEsc(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  // Clamp to viewport
  const style = {
    position: 'fixed', zIndex: 9999,
    left: Math.min(x, window.innerWidth - 220),
    top:  Math.min(y, window.innerHeight - 300),
    minWidth: 200,
    background: 'var(--panel)', border: '1px solid var(--border)',
    borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    padding: '4px 0', animation: 'fadein 0.12s ease',
  }

  return <div ref={ref} style={style}>{children}</div>
}

export function MenuItem({ icon, label, danger, disabled, onClick }) {
  return (
    <button onClick={disabled ? undefined : onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', border: 'none', background: 'transparent',
      color: disabled ? 'var(--t3)' : danger ? '#EF4444' : 'var(--t2)',
      fontSize: 12, fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1, textAlign: 'left',
    }}
    onMouseOver={e => { if (!disabled) e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{icon}</span>
      {label}
    </button>
  )
}

export function MenuDivider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '4px 8px' }} />
}

export function MenuHeader({ label }) {
  return (
    <div style={{
      padding: '6px 12px 4px', fontSize: 10, color: 'var(--t3)',
      fontFamily: 'var(--mono)', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}>
      {label}
    </div>
  )
}

// ── User Context Menu (right-click on avatar/username) ──
export function UserContextMenu({ x, y, targetUser, roomId, onClose, onAction }) {
  const { user, authHeader } = useUserStore()
  const isMod = ['owner','co_creator','admin','moderator'].includes(user?.role)
  const isAdmin = ['owner','co_creator','admin'].includes(user?.role)
  const isSelf = targetUser?.id === user?.id || targetUser?.username === user?.username
  const [subMenu, setSubMenu] = useState(null) // 'timeout' | 'warn' | 'report'
  const [reason, setReason] = useState('')
  const [duration, setDuration] = useState('10m')
  const [reportCat, setReportCat] = useState('other')
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState(null)

  async function doAction(action, body = {}) {
    setLoading(true)
    try {
      const res = await fetch(`${API}/moderation/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ userId: targetUser.id ?? targetUser.userId, roomId, ...body }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setMsg(`✓ ${action}`)
      onAction?.(action, data)
      setTimeout(onClose, 800)
    } catch (e) {
      setMsg(`✗ ${e.message}`)
    }
    setLoading(false)
  }

  // Sub-menus
  if (subMenu === 'timeout') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label="Timeout User" />
        {TIMEOUT_DURATIONS.map(d => (
          <MenuItem key={d.value} icon="⏱" label={d.label}
            onClick={() => { setDuration(d.value); setSubMenu('timeout-reason') }} />
        ))}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu(null)} />
      </ContextMenu>
    )
  }

  if (subMenu === 'timeout-reason') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label={`Timeout — ${duration}`} />
        <div style={{ padding: '8px 12px' }}>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Reason (optional)" autoFocus
            style={{ width:'100%', padding:'7px 9px', boxSizing:'border-box',
              background:'var(--card)', border:'1px solid var(--border)',
              borderRadius:5, color:'var(--t1)', fontSize:12, fontFamily:'inherit' }}
            onKeyDown={e => { if (e.key === 'Enter') doAction('timeout', { duration, reason }) }}
          />
        </div>
        <MenuItem icon="⏱" label={loading ? 'Applying…' : 'Apply Timeout'} danger
          disabled={loading} onClick={() => doAction('timeout', { duration, reason })} />
        {msg && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--t3)' }}>{msg}</div>}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu('timeout')} />
      </ContextMenu>
    )
  }

  if (subMenu === 'warn') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label="Warn User" />
        <div style={{ padding: '8px 12px' }}>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Warning reason (required)" autoFocus
            style={{ width:'100%', padding:'7px 9px', boxSizing:'border-box',
              background:'var(--card)', border:'1px solid var(--border)',
              borderRadius:5, color:'var(--t1)', fontSize:12, fontFamily:'inherit' }}
            onKeyDown={e => { if (e.key === 'Enter' && reason.trim()) doAction('warn', { reason }) }}
          />
        </div>
        <MenuItem icon="⚠️" label={loading ? 'Sending…' : 'Send Warning'} danger
          disabled={loading || !reason.trim()} onClick={() => doAction('warn', { reason })} />
        {msg && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--t3)' }}>{msg}</div>}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu(null)} />
      </ContextMenu>
    )
  }

  if (subMenu === 'ban') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label="Ban User" />
        <div style={{ padding: '8px 12px' }}>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Ban reason (required)" autoFocus
            style={{ width:'100%', padding:'7px 9px', boxSizing:'border-box',
              background:'var(--card)', border:'1px solid rgba(239,68,68,0.4)',
              borderRadius:5, color:'var(--t1)', fontSize:12, fontFamily:'inherit' }}
            onKeyDown={e => { if (e.key === 'Enter' && reason.trim()) doAction('ban', { reason }) }}
          />
        </div>
        <MenuItem icon="🚫" label={loading ? 'Banning…' : 'Confirm Ban'} danger
          disabled={loading || !reason.trim()} onClick={() => doAction('ban', { reason })} />
        {msg && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--t3)' }}>{msg}</div>}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu(null)} />
      </ContextMenu>
    )
  }

  if (subMenu === 'report') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label="Report User" />
        <div style={{ padding: '4px 12px 0' }}>
          {REPORT_CATEGORIES.map(c => (
            <label key={c.value} style={{
              display:'flex', alignItems:'center', gap:8, padding:'5px 0',
              fontSize:12, color: reportCat === c.value ? 'var(--t1)' : 'var(--t3)', cursor:'pointer',
            }}>
              <input type="radio" name="cat" checked={reportCat === c.value}
                onChange={() => setReportCat(c.value)}
                style={{ accentColor: 'var(--blue)' }} />
              {c.label}
            </label>
          ))}
        </div>
        <div style={{ padding: '6px 12px' }}>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Details (required)" autoFocus
            style={{ width:'100%', padding:'7px 9px', boxSizing:'border-box',
              background:'var(--card)', border:'1px solid var(--border)',
              borderRadius:5, color:'var(--t1)', fontSize:12, fontFamily:'inherit' }}
            onKeyDown={e => { if (e.key === 'Enter' && reason.trim()) {
              doAction('report', { targetId: targetUser.id, targetType:'user', reason, category: reportCat })
            }}}
          />
        </div>
        <MenuItem icon="📨" label={loading ? 'Sending…' : 'Submit Report'}
          disabled={loading || !reason.trim()}
          onClick={() => doAction('report', { targetId: targetUser.id, targetType:'user', reason, category: reportCat })} />
        {msg && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--t3)' }}>{msg}</div>}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu(null)} />
      </ContextMenu>
    )
  }

  // ── Main menu ──
  return (
    <ContextMenu x={x} y={y} onClose={onClose}>
      {/* User header */}
      <div style={{ padding:'10px 12px 6px', display:'flex', alignItems:'center', gap:8 }}>
        <div style={{
          width:28, height:28, borderRadius:'50%',
          background:`${targetUser.avatarColor ?? targetUser.color ?? '#38BDF8'}18`,
          border:`1.5px solid ${targetUser.avatarColor ?? targetUser.color ?? '#38BDF8'}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:10, fontWeight:700, color: targetUser.avatarColor ?? targetUser.color ?? '#38BDF8',
          fontFamily:'var(--mono)',
        }}>
          {(targetUser.username ?? targetUser.user ?? '?')[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--t1)' }}>
            {targetUser.username ?? targetUser.user}
          </div>
          {targetUser.role && (
            <div style={{ fontSize:9, color:'var(--t3)', fontFamily:'var(--mono)', textTransform:'uppercase' }}>
              {targetUser.role}
            </div>
          )}
        </div>
      </div>
      <MenuDivider />

      {/* Everyone can do these */}
      <MenuItem icon="👤" label="View Profile"
        onClick={() => { window.location.href = `/app/profile/${targetUser.username ?? targetUser.user}`; onClose() }} />
      {!isSelf && (
        <MenuItem icon="💬" label="Direct Message"
          onClick={() => { window.location.href = `/app/messages/${targetUser.id ?? targetUser.userId}`; onClose() }} />
      )}

      {/* Report (anyone, not self) */}
      {!isSelf && (
        <>
          <MenuDivider />
          <MenuItem icon="🚩" label="Report User" onClick={() => setSubMenu('report')} />
        </>
      )}

      {/* Mod tools */}
      {isMod && !isSelf && (
        <>
          <MenuDivider />
          <MenuHeader label="Moderation" />
          <MenuItem icon="⚠️" label="Warn"    onClick={() => setSubMenu('warn')} />
          <MenuItem icon="⏱" label="Timeout" onClick={() => setSubMenu('timeout')} />
          <MenuItem icon="🔇" label="Mute"
            onClick={() => doAction('mute', { reason: 'Muted by moderator' })} />
        </>
      )}

      {/* Admin tools */}
      {isAdmin && !isSelf && (
        <>
          <MenuItem icon="🔄" label="Remove Timeout"
            onClick={() => {
              fetch(`${API}/moderation/penalty/timeout-${targetUser.id}`, {
                method: 'DELETE', headers: authHeader()
              }).then(() => { setMsg('✓ timeout removed'); setTimeout(onClose, 800) })
            }} />
          <MenuItem icon="🚫" label="Ban User" danger onClick={() => setSubMenu('ban')} />
        </>
      )}

      {msg && (
        <>
          <MenuDivider />
          <div style={{ padding:'6px 12px', fontSize:11, color:'var(--green)', fontFamily:'var(--mono)' }}>{msg}</div>
        </>
      )}
    </ContextMenu>
  )
}

// ── Message Context Menu (right-click on a message) ──
export function MessageContextMenu({ x, y, message, roomId, onClose, onAction }) {
  const { user, authHeader } = useUserStore()
  const isMod = ['owner','co_creator','admin','moderator'].includes(user?.role)
  const isSelf = message.userId === user?.id || message.username === user?.username
  const [reason, setReason] = useState('')
  const [subMenu, setSubMenu] = useState(null)
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState(null)

  async function deleteMessage() {
    setLoading(true)
    try {
      const res = await fetch(`${API}/moderation/delete-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ messageId: message.id, roomId, reason }),
      })
      if (!res.ok) throw new Error('Failed')
      setMsg('✓ deleted')
      onAction?.('delete_message', { messageId: message.id })
      setTimeout(onClose, 600)
    } catch (e) {
      setMsg(`✗ ${e.message}`)
    }
    setLoading(false)
  }

  if (subMenu === 'delete') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label="Delete Message" />
        <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--t3)', lineHeight: 1.5 }}>
          "{(message.content ?? message.msg ?? '').slice(0, 80)}"
        </div>
        <div style={{ padding: '4px 12px 8px' }}>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Reason (optional)" autoFocus
            style={{ width:'100%', padding:'7px 9px', boxSizing:'border-box',
              background:'var(--card)', border:'1px solid var(--border)',
              borderRadius:5, color:'var(--t1)', fontSize:12, fontFamily:'inherit' }}
            onKeyDown={e => { if (e.key === 'Enter') deleteMessage() }}
          />
        </div>
        <MenuItem icon="🗑" label={loading ? 'Deleting…' : 'Confirm Delete'} danger
          disabled={loading} onClick={deleteMessage} />
        {msg && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--t3)' }}>{msg}</div>}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu(null)} />
      </ContextMenu>
    )
  }

  if (subMenu === 'report') {
    return (
      <ContextMenu x={x} y={y} onClose={onClose}>
        <MenuHeader label="Report Message" />
        <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--t3)', lineHeight: 1.5 }}>
          "{(message.content ?? message.msg ?? '').slice(0, 80)}"
        </div>
        <div style={{ padding: '4px 12px 8px' }}>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Why are you reporting this?" autoFocus
            style={{ width:'100%', padding:'7px 9px', boxSizing:'border-box',
              background:'var(--card)', border:'1px solid var(--border)',
              borderRadius:5, color:'var(--t1)', fontSize:12, fontFamily:'inherit' }}
            onKeyDown={e => {
              if (e.key === 'Enter' && reason.trim()) {
                setLoading(true)
                fetch(`${API}/moderation/report`, {
                  method:'POST',
                  headers:{'Content-Type':'application/json',...authHeader()},
                  body:JSON.stringify({
                    targetId: message.userId, targetType:'message',
                    targetRef: message.id, reason, roomId,
                  }),
                }).then(() => { setMsg('✓ reported'); setTimeout(onClose, 800) })
                  .catch(() => setMsg('✗ failed'))
                  .finally(() => setLoading(false))
              }
            }}
          />
        </div>
        <MenuItem icon="📨" label={loading ? 'Sending…' : 'Submit Report'}
          disabled={loading || !reason.trim()}
          onClick={() => {
            setLoading(true)
            fetch(`${API}/moderation/report`, {
              method:'POST',
              headers:{'Content-Type':'application/json',...authHeader()},
              body:JSON.stringify({
                targetId: message.userId, targetType:'message',
                targetRef: message.id, reason, roomId,
              }),
            }).then(() => { setMsg('✓ reported'); setTimeout(onClose, 800) })
              .catch(() => setMsg('✗ failed'))
              .finally(() => setLoading(false))
          }} />
        {msg && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--t3)' }}>{msg}</div>}
        <MenuDivider />
        <MenuItem icon="←" label="Back" onClick={() => setSubMenu(null)} />
      </ContextMenu>
    )
  }

  return (
    <ContextMenu x={x} y={y} onClose={onClose}>
      {/* Copy text */}
      <MenuItem icon="📋" label="Copy Text"
        onClick={() => { navigator.clipboard.writeText(message.content ?? message.msg ?? ''); onClose() }} />

      {/* Report (not self) */}
      {!isSelf && (
        <MenuItem icon="🚩" label="Report Message" onClick={() => setSubMenu('report')} />
      )}

      {/* Mod actions */}
      {isMod && (
        <>
          <MenuDivider />
          <MenuHeader label="Moderation" />
          <MenuItem icon="🗑" label="Delete Message" danger onClick={() => setSubMenu('delete')} />
          {!isSelf && (
            <MenuItem icon="⚠️" label={`Warn ${message.username ?? message.user}`}
              onClick={() => {
                fetch(`${API}/moderation/warn`, {
                  method:'POST',
                  headers:{'Content-Type':'application/json',...authHeader()},
                  body:JSON.stringify({ userId: message.userId, reason: 'Warning from chat message', roomId }),
                }).then(() => { setMsg('✓ warned'); setTimeout(onClose, 800) })
              }} />
          )}
          {!isSelf && (
            <MenuItem icon="⏱" label={`Timeout ${message.username ?? message.user}`}
              onClick={() => {
                fetch(`${API}/moderation/timeout`, {
                  method:'POST',
                  headers:{'Content-Type':'application/json',...authHeader()},
                  body:JSON.stringify({ userId: message.userId, duration: '10m', reason: 'Timeout from chat', roomId }),
                }).then(() => { setMsg('✓ timed out'); setTimeout(onClose, 800) })
              }} />
          )}
        </>
      )}

      {msg && (
        <>
          <MenuDivider />
          <div style={{ padding:'6px 12px', fontSize:11, color:'var(--green)', fontFamily:'var(--mono)' }}>{msg}</div>
        </>
      )}
    </ContextMenu>
  )
}
